import React, {useEffect, useRef, useState} from 'react'
import {Alert, Button, Col, message, Row} from "antd";
import {PlaySquareOutlined} from "@ant-design/icons";
import {useSearchParams} from "react-router-dom";
import {useTranslations} from "../../lang";
import api from "../../api";
import {CONSENT_CHANGED_EVENT, createFunnelEventId, trackFunnelEvent} from "../../analytics";

const volatilePurchaseClaims = new Set()
const PURCHASE_CLAIM_TTL_MS = 5 * 60 * 1000

const purchaseKey = (kind, transactionId) => `oogst_purchase_${kind}:${transactionId}`

function claimPurchase(transactionId) {
    if (volatilePurchaseClaims.has(transactionId)) return null
    volatilePurchaseClaims.add(transactionId)
    const token = createFunnelEventId('purchase_claim', transactionId + ':' + Date.now())
    try {
        if (window.localStorage.getItem(purchaseKey('sent', transactionId)) === 'true') {
            volatilePurchaseClaims.delete(transactionId)
            return null
        }
        const existing = JSON.parse(window.localStorage.getItem(purchaseKey('claim', transactionId)) || 'null')
        if (existing?.claimed_at && Date.now() - existing.claimed_at < PURCHASE_CLAIM_TTL_MS) {
            volatilePurchaseClaims.delete(transactionId)
            return null
        }
        const claim = JSON.stringify({token, claimed_at: Date.now()})
        window.localStorage.setItem(purchaseKey('claim', transactionId), claim)
        if (window.localStorage.getItem(purchaseKey('claim', transactionId)) !== claim) {
            volatilePurchaseClaims.delete(transactionId)
            return null
        }
    } catch {
        // The module-scoped claim still prevents duplicate sends in this browser runtime.
    }
    return token
}

function releasePurchaseClaim(transactionId, token, sent) {
    if (!sent) volatilePurchaseClaims.delete(transactionId)
    let durableSent = false
    try {
        if (sent) {
            window.localStorage.setItem(purchaseKey('sent', transactionId), 'true')
            durableSent = window.localStorage.getItem(purchaseKey('sent', transactionId)) === 'true'
        }
        const claimKey = purchaseKey('claim', transactionId)
        const existing = JSON.parse(window.localStorage.getItem(claimKey) || 'null')
        if (existing?.token === token) window.localStorage.removeItem(claimKey)
    } catch {
        // The volatile sent claim intentionally remains when durable storage is unavailable.
    }
    if (sent && durableSent) volatilePurchaseClaims.delete(transactionId)
}

async function withPurchaseClaim(transactionId, send) {
    const execute = () => {
        const claim = claimPurchase(transactionId)
        if (!claim) return false
        const sent = send()
        releasePurchaseClaim(transactionId, claim, sent)
        return sent
    }
    if (navigator.locks?.request) {
        return navigator.locks.request(
            `oogst_purchase:${transactionId}`,
            {ifAvailable: true},
            lock => lock ? execute() : false,
        )
    }
    return execute()
}

function getPurchaseClaimRetryDelay(transactionId) {
    try {
        if (window.localStorage.getItem(purchaseKey('sent', transactionId)) === 'true') return null
        const claim = JSON.parse(window.localStorage.getItem(purchaseKey('claim', transactionId)) || 'null')
        if (!claim?.claimed_at) return null
        return Math.max(0, PURCHASE_CLAIM_TTL_MS - (Date.now() - claim.claimed_at)) + 25
    } catch {
        return null
    }
}

export default function PaymentSuccess() {

    const {translate} = useTranslations()
    const [searchParams] = useSearchParams();
    const [pendingPurchase, setPendingPurchase] = useState(null)
    const transactionId = searchParams.get('id')
    const purchaseSent = useRef((() => {
        try {
            return window.localStorage.getItem(`oogst_purchase_sent:${transactionId}`) === 'true'
        } catch {
            return false
        }
    })())

    useEffect(() => {
        if (!transactionId) return

        let active = true
        api.getPaymentAnalytics(transactionId).then(payment => {
            const numericAmount = Number(payment.amount)
            if (
                !active
                || !Number.isFinite(numericAmount)
                || numericAmount < 0
                || !payment.transaction_id
                || !['EUR', 'USD'].includes(payment.currency)
            ) {
                return
            }
            setPendingPurchase({
                event_id: createFunnelEventId('purchase', payment.transaction_id),
                value: numericAmount,
                transaction_id: payment.transaction_id,
                currency: payment.currency,
                items: [{item_id: 'oogst-app', item_name: 'Oogst.app subscription'}],
            })
        }).catch(() => {})

        return () => { active = false }
    }, [transactionId]);

    useEffect(() => {
        if (!pendingPurchase || purchaseSent.current) return
        let retryTimer = null

        const sendPendingPurchase = async () => {
            if (purchaseSent.current) return
            purchaseSent.current = await withPurchaseClaim(
                pendingPurchase.transaction_id,
                () => trackFunnelEvent('purchase', pendingPurchase),
            )
            if (!purchaseSent.current) {
                const retryDelay = getPurchaseClaimRetryDelay(pendingPurchase.transaction_id)
                if (retryDelay !== null) retryTimer = window.setTimeout(sendPendingPurchase, retryDelay)
            }
        }
        sendPendingPurchase()
        window.addEventListener(CONSENT_CHANGED_EVENT, sendPendingPurchase)
        return () => {
            window.removeEventListener(CONSENT_CHANGED_EVENT, sendPendingPurchase)
            if (retryTimer !== null) window.clearTimeout(retryTimer)
        }
    }, [pendingPurchase]);

    return (
        <Row>
            <Col className={"text-center"} span={24}>
                <h1>{translate('Betaling gelukt!')}</h1>
                <Alert type={"success"}
                       description={translate("Je betaling is gelukt! Je kan nu gebruik maken van Oogst.app!")}></Alert>
                <br/>
                <h1>{translate('Happy Gardening!')}</h1>
                <img alt="Happy gardening" style={{maxWidth: '100%'}} src={"/static/img/happy-gardening.webp"}/>
                <br/><br/>
                <h1>{translate('Hoe nu verder?')}</h1>
                <p>
                    {translate('Rechtsboven staat een knop, waarmee je een korte instructievideo kunt kijken over dat deel van Oogst.app. Hij ziet er zo uit:')}
                </p>
                <Button
                    onClick={() => message.info(translate('Dit is een voorbeeldknop ;-) Klik op de knop rechtsboven in het menu om hem in actie te zien'))}>
                    <PlaySquareOutlined/> {translate('Hoe werkt dit?')}
                </Button>

            </Col>


        </Row>
    )
}